/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PaymentTracePage
 * ----------------
 * Everything about one payment on one screen (spec §6.4): the order header, its
 * line items, the status timeline showing what caused each hop, and every
 * webhook event with its raw payload — plus the replay action (§6.5).
 *
 * Contract notes that shape this page:
 *  - Money arrives as decimal STRINGS. Formatted here, never summed.
 *  - `timeline[].seconds_in_previous_state` is null on the first hop only.
 *  - `payouts` is always present and empty while every event is self-organized.
 *  - Replay re-triggers real payment processing, so it is gated behind a
 *    type-to-confirm dialog rather than a plain "are you sure".
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useAlertDialog } from '../components/common/AlertDialogContext';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret
});

function formatMoney(value, currency) {
    if (value == null) return '—';
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    const f = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${currency} ${f}` : f;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

/** Seconds → a duration a human reads at a glance. */
function formatDuration(seconds) {
    if (seconds == null) return null;
    const s = Math.max(0, Number(seconds) || 0);
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
    return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}

const STATUS_STYLE = {
    paid: { bg: '#dcfce7', fg: '#166534', border: '#bbf7d0', icon: 'bi-check-circle-fill' },
    pending: { bg: '#e0e7ff', fg: '#3730a3', border: '#c7d2fe', icon: 'bi-hourglass-split' },
    in_progress: { bg: '#e0f2fe', fg: '#075985', border: '#bae6fd', icon: 'bi-arrow-repeat' },
    cancelled: { bg: '#fee2e2', fg: '#991b1b', border: '#fecaca', icon: 'bi-x-circle-fill' },
    returned: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a', icon: 'bi-arrow-counterclockwise' },
    other: { bg: '#f1f5f9', fg: '#334155', border: '#e2e8f0', icon: 'bi-question-circle' }
};

const EVENT_STYLE = {
    processed: { fg: '#166534', bg: '#dcfce7', border: '#bbf7d0', icon: 'bi-check-circle' },
    received: { fg: '#075985', bg: '#e0f2fe', border: '#bae6fd', icon: 'bi-inbox' },
    ignored: { fg: '#475569', bg: '#f1f5f9', border: '#e2e8f0', icon: 'bi-slash-circle' },
    signature_invalid: { fg: '#92400e', bg: '#fef3c7', border: '#fde68a', icon: 'bi-shield-exclamation' },
    failed: { fg: '#991b1b', bg: '#fee2e2', border: '#fecaca', icon: 'bi-x-circle' }
};

function StatusPill({ status }) {
    const s = STATUS_STYLE[status] || STATUS_STYLE.other;
    return (
        <span className="badge d-inline-flex align-items-center gap-1"
            style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}`, fontWeight: 700 }}>
            <i className={`bi ${s.icon}`} aria-hidden="true"></i>{status || 'unknown'}
        </span>
    );
}

function EventPill({ status }) {
    const s = EVENT_STYLE[status] || EVENT_STYLE.ignored;
    return (
        <span className="badge d-inline-flex align-items-center gap-1"
            style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}`, fontWeight: 600 }}>
            <i className={`bi ${s.icon}`} aria-hidden="true"></i>
            {(status || 'unknown').replace(/_/g, ' ')}
        </span>
    );
}

function Card({ title, icon, iconColor, subtitle, actions, children, theme }) {
    return (
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: theme.cardRadius }}>
            <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${icon}`} aria-hidden="true" style={{ fontSize: '1.2rem', color: iconColor }}></i>
                        <h6 className="fw-bold mb-0 text-dark">{title}</h6>
                    </div>
                    {actions}
                </div>
                {subtitle && <div className="text-muted mt-1" style={{ fontSize: '12px' }}>{subtitle}</div>}
            </div>
            <div className="card-body px-4 pt-3">{children}</div>
        </div>
    );
}

/**
 * §6.5 — type-to-confirm. Replay re-runs real payment processing (fulfilment,
 * internal events), so it must not be firable by a stray click the way a plain
 * confirm popup can be.
 */
function ReplayDialog({ event, theme, busy, onCancel, onConfirm }) {
    const [typed, setTyped] = useState('');
    const ready = typed === 'REPLAY';
    const wrong = typed.length > 0 && !'REPLAY'.startsWith(typed);

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1080,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
        }}>
            <div className="bg-white" role="dialog" aria-modal="true" aria-labelledby="replay-title"
                style={{ width: 520, maxWidth: '100%', borderRadius: 10, boxShadow: '0 18px 44px rgba(15,23,42,0.32)' }}>
                <div className="p-4 pb-0 d-flex gap-3">
                    <div className="flex-shrink-0 d-flex align-items-center justify-content-center"
                        style={{ width: 38, height: 38, borderRadius: '50%', background: '#fef3c7' }}>
                        <i className="bi bi-arrow-clockwise" aria-hidden="true" style={{ color: '#b45309', fontSize: '1.2rem' }}></i>
                    </div>
                    <div>
                        <h5 id="replay-title" className="fw-bold mb-1" style={{ fontSize: '17px' }}>Replay this webhook event?</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '13px', lineHeight: 1.55 }}>
                            This re-runs payment processing for event <code className="fw-bold">#{event.id}</code>
                            {' '}(<code>{event.event_type}</code>) against its original stored payload.
                        </p>
                    </div>
                </div>

                <div className="mx-4 mt-3 p-3" style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7 }}>
                    <div className="fw-bold text-uppercase mb-2" style={{ fontSize: '11px', letterSpacing: '0.4px', color: '#92400e' }}>
                        What runs again
                    </div>
                    <ul className="mb-0 ps-3" style={{ fontSize: '12.5px', lineHeight: 1.7, color: '#78350f' }}>
                        <li>The live webhook handler — idempotent, so a second success is a no-op</li>
                        <li>Line-item fulfilment and any internal events it fires</li>
                        <li>A new status transition, attributed to this event</li>
                    </ul>
                </div>

                <div className="px-4 pt-3">
                    <label htmlFor="replay-confirm" className="form-label fw-semibold" style={{ fontSize: '12.5px' }}>
                        Type <code className="fw-bold">REPLAY</code> to confirm
                    </label>
                    <input id="replay-confirm" type="text" autoComplete="off" spellCheck="false"
                        className="form-control" value={typed} placeholder="REPLAY"
                        onChange={e => setTyped(e.target.value)}
                        style={{
                            fontFamily: 'monospace', letterSpacing: 1,
                            borderColor: wrong ? '#fca5a5' : (ready ? theme.primary : '#cbd5e1')
                        }} />
                    <div className="mt-2" style={{ fontSize: '11.5px', minHeight: 18, color: wrong ? '#b91c1c' : (ready ? '#166534' : '#64748b') }}>
                        {wrong ? 'Must match REPLAY exactly, in capitals.'
                            : ready ? 'Confirmed — Replay is now live.'
                                : 'The button stays disabled until this matches.'}
                    </div>
                </div>

                <div className="px-4 py-3 mt-2 d-flex justify-content-end gap-2"
                    style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 10px 10px' }}>
                    <button type="button" className="btn btn-light" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button type="button" className="btn" disabled={!ready || busy}
                        onClick={() => onConfirm(event)}
                        style={{ background: ready ? theme.primary : '#94a3b8', color: '#fff', fontWeight: 700 }}>
                        {busy ? 'Replaying…' : 'Replay'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function PaymentTracePage() {
    const { theme } = useTheme();
    const { id: applicationId } = useParams();
    const navigate = useNavigate();
    const { showAlertDialog } = useAlertDialog();
    const [searchParams] = useSearchParams();

    const [identifier, setIdentifier] = useState(searchParams.get('q') || '');
    const [trace, setTrace] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [replayTarget, setReplayTarget] = useState(null);
    const [replayBusy, setReplayBusy] = useState(false);

    // Only the newest lookup may write state — a slow earlier search must not
    // overwrite a newer one with a different payment.
    const reqId = useRef(0);

    const lookup = useCallback(async (ident) => {
        const q = (ident || '').trim();
        if (!q) return;
        const myId = ++reqId.current;
        setLoading(true);
        setError(null);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/payments/trace/${encodeURIComponent(q)}/?application_id=${applicationId}`,
                { method: 'GET' }
            );
            if (!resp.ok) {
                const body = await resp.json().catch(() => ({}));
                throw new Error(body.error || `Lookup failed (${resp.status})`);
            }
            const body = await resp.json();
            if (reqId.current !== myId) return;
            setTrace(body);
            setExpanded(null);
        } catch (e) {
            if (reqId.current !== myId) return;
            setTrace(null);
            setError(e.message || 'Lookup failed');
        } finally {
            if (reqId.current === myId) setLoading(false);
        }
    }, [applicationId]);

    const doReplay = useCallback(async (event) => {
        setReplayBusy(true);
        try {
            const resp = await apiHelper.apiCall(
                `/arcee/payments/webhooks/events/${event.id}/replay/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ application_id: parseInt(applicationId, 10) })
                }
            );
            const body = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(body.error || `Replay failed (${resp.status})`);

            setReplayTarget(null);
            showAlertDialog({
                title: 'Replay complete',
                message: `Event #${body.replayed_event_id} reprocessed — status ${body.processing_status}`
                    + (body.payment_status ? `, payment now ${body.payment_status}.` : '.'),
                type: 'success'
            });
            await lookup(identifier);
        } catch (e) {
            showAlertDialog({ title: 'Replay failed', message: e.message, type: 'error' });
        } finally {
            setReplayBusy(false);
        }
    }, [applicationId, identifier, lookup, showAlertDialog]);

    // Arriving with ?q= (from the violation list's "Trace →") should SHOW the
    // trace, not just pre-fill the box and leave an apparently empty page.
    const autoQuery = searchParams.get('q');
    useEffect(() => {
        if (autoQuery) lookup(autoQuery);
    }, [autoQuery, lookup]);

    const currency = trace?.payment?.currency || '';

    return (
        <div className="d-flex flex-column w-100" style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>

            <div className="w-100 px-3 py-3 px-md-4 py-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                    <button className="btn btn-light rounded-circle shadow-sm" aria-label="Back to application"
                        onClick={() => navigate(`/dashboard/applications/${applicationId}`)}>
                        <i className="bi bi-arrow-left" aria-hidden="true"></i>
                    </button>
                    <div>
                        <h4 className="mb-0 fw-bold text-dark">Payment Trace</h4>
                        <small className="text-muted d-none d-md-block">Everything about one payment, on one screen.</small>
                    </div>
                </div>
                <button className="btn d-flex align-items-center gap-2"
                    onClick={() => navigate(`/dashboard/applications/${applicationId}/payment-debugging`)}
                    style={{ border: `1px solid ${theme.primary}`, color: theme.primary, borderRadius: theme.buttonRadius }}>
                    <i className="bi bi-activity" aria-hidden="true"></i>Alerts &amp; Debugging
                </button>
            </div>

            <div className="container-fluid p-3 p-md-4" style={{ maxWidth: 1400 }}>

                <form className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius }}
                    onSubmit={e => { e.preventDefault(); lookup(identifier); }}>
                    <div className="card-body py-3 px-4 d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                        <label htmlFor="trace-id" className="small fw-bold text-muted mb-0">Find</label>
                        <input id="trace-id" type="search" className="form-control form-control-sm"
                            value={identifier} onChange={e => setIdentifier(e.target.value)}
                            placeholder="gateway_order_id, Payment id, or gateway_payment_id"
                            style={{ maxWidth: 460, fontFamily: 'monospace', borderRadius: theme.inputRadius || '6px' }} />
                        <button type="submit" className="btn btn-sm" disabled={loading || !identifier.trim()}
                            style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: theme.buttonRadius }}>
                            <i className="bi bi-search me-1" aria-hidden="true"></i>Trace
                        </button>
                    </div>
                </form>

                {loading && <ProgressBarDialog open={true} text_message="Tracing payment..." />}

                {!loading && error && (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body text-center py-5">
                            <i className="bi bi-search d-block mb-3" aria-hidden="true" style={{ fontSize: '2rem', color: '#dc2626' }}></i>
                            <div className="fw-semibold text-dark mb-1">No trace</div>
                            <div className="text-muted small">{error}</div>
                        </div>
                    </div>
                )}

                {!loading && !error && !trace && (
                    <div className="card border-0 shadow-sm" style={{ borderRadius: theme.cardRadius }}>
                        <div className="card-body text-center py-5">
                            <i className="bi bi-upc-scan d-block mb-3" aria-hidden="true" style={{ fontSize: '2rem', color: '#94a3b8' }}></i>
                            <div className="fw-semibold text-dark mb-1">Search for a payment</div>
                            <div className="text-muted small">
                                Any of the three identifiers support is likely to have works.
                            </div>
                        </div>
                    </div>
                )}

                {!loading && trace && (
                    <>
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: theme.cardRadius }}>
                            <div className="card-body px-4 py-4">
                                <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
                                    <div>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <code className="fw-bold" style={{ fontSize: '17px' }}>
                                                {trace.payment.gateway_order_id || '—'}
                                            </code>
                                            <StatusPill status={trace.payment.status} />
                                        </div>
                                        <div className="d-flex flex-wrap gap-4" style={{ fontSize: '12.5px' }}>
                                            <Field label="Payment id" value={<code>{trace.payment.id}</code>} />
                                            <Field label="gateway_payment_id" value={<code>{trace.payment.gateway_payment_id || '—'}</code>} />
                                            <Field label="Created" value={formatDateTime(trace.payment.created_at)} />
                                            <Field label="Completed" value={formatDateTime(trace.payment.completed_at)} />
                                            {trace.payment.failure_reason && (
                                                <Field label="Failure reason" value={<span className="text-danger">{trace.payment.failure_reason}</span>} />
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <div className="text-muted" style={{ fontSize: '12px' }}>
                                            Gross {formatMoney(trace.payment.gross_amount, currency)} · Discount {formatMoney(trace.payment.discount_amount, currency)}
                                        </div>
                                        <div className="fw-bold" style={{ fontSize: '26px', color: theme.primary }}>
                                            {formatMoney(trace.payment.amount, currency)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-4 mb-4">
                            <div className="col-12 col-lg-7">
                                <Card title="Line items" icon="bi-list-ul" iconColor={theme.primary} theme={theme}>
                                    {!trace.line_items.length ? (
                                        <div className="text-muted text-center py-4 small">No line items.</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-sm align-middle mb-0">
                                                <thead>
                                                    <tr style={{ fontSize: '12px', color: theme.primary }}>
                                                        <th className="fw-semibold">Entity</th>
                                                        <th className="fw-semibold">Parent</th>
                                                        <th className="fw-semibold">Organizer</th>
                                                        <th className="fw-semibold text-end">Fee</th>
                                                        <th className="fw-semibold text-end">Net</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {trace.line_items.map(li => (
                                                        <tr key={li.id} style={{ fontSize: '12.5px' }}>
                                                            <td>{li.chargeable_entity || '—'}</td>
                                                            <td><code>{li.chargeable_entity_parent_id || '—'}</code></td>
                                                            <td>
                                                                <span className="badge" style={{ background: '#f1f5f9', color: '#334155', fontWeight: 600 }}>
                                                                    {li.is_self_organized ? 'Self-Organized' : `Organizer ${li.organizer_instance_id}`}
                                                                </span>
                                                            </td>
                                                            <td className="text-end text-muted">{formatMoney(li.applied_platform_fee_amount, currency)}</td>
                                                            <td className="text-end fw-semibold">{formatMoney(li.net_amount, currency)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            <div className="mt-3 px-3 py-2" style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 6, fontSize: '12px' }}>
                                                <strong className="text-dark">Payout / settlement</strong> — {trace.payouts.length
                                                    ? `${trace.payouts.length} payout row(s).`
                                                    : 'no rows. Every line item here is self-organized, so the application is seller of record and there is no third party to pay.'}
                                            </div>
                                        </div>
                                    )}
                                </Card>
                            </div>

                            <div className="col-12 col-lg-5">
                                <Card title="Status timeline" icon="bi-clock-history" iconColor="#7c3aed"
                                    subtitle="Never just “it changed” — every hop names what caused it" theme={theme}>
                                    {!trace.timeline.length ? (
                                        <div className="text-muted text-center py-4 small">
                                            No transitions recorded. This payment predates transition logging.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column">
                                            {trace.timeline.map((h, i) => {
                                                const dwell = formatDuration(h.seconds_in_previous_state);
                                                const last = i === trace.timeline.length - 1;
                                                const dot = h.is_unexpected ? '#dc2626'
                                                    : h.to_status === 'paid' ? '#16a34a' : '#7986cb';
                                                return (
                                                    <div key={h.id} className="d-flex gap-3">
                                                        <div className="d-flex flex-column align-items-center" style={{ width: 14 }}>
                                                            <div style={{ width: 11, height: 11, borderRadius: '50%', background: dot, flexShrink: 0 }}></div>
                                                            {!last && <div style={{ width: 2, flexGrow: 1, background: '#e2e8f0', minHeight: 30 }}></div>}
                                                        </div>
                                                        <div className="flex-grow-1 pb-3">
                                                            <div className="fw-bold" style={{ fontSize: '13px' }}>
                                                                <code>{h.from_status || '—'}</code> → <code>{h.to_status}</code>
                                                                {h.is_unexpected && (
                                                                    <span className="badge ms-2" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}>
                                                                        outside the map
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-muted" style={{ fontSize: '11.5px' }}>
                                                                {formatDateTime(h.created_at)}
                                                                {dwell && <> · <strong style={{ color: '#b45309' }}>{dwell} in previous state</strong></>}
                                                            </div>
                                                            <div className="mt-1" style={{ fontSize: '11px' }}>
                                                                {h.triggered_by_webhook_event_id ? (
                                                                    <span className="badge" style={{ background: '#e8eaf6', color: theme.primary, fontWeight: 600 }}>
                                                                        webhook event #{h.triggered_by_webhook_event_id}
                                                                    </span>
                                                                ) : h.triggered_by_admin ? (
                                                                    <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontWeight: 600 }}>
                                                                        admin · {h.triggered_by_admin.username}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">{h.reason || 'no trigger recorded'}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </Card>
                            </div>
                        </div>

                        <Card title="Webhook events" icon="bi-plug" iconColor={theme.primary}
                            subtitle="Every inbound call from the gateway for this payment, verbatim" theme={theme}>
                            {!trace.webhook_events.length ? (
                                <div className="text-muted text-center py-4 small">No webhook events for this payment.</div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead>
                                            <tr style={{ fontSize: '12px', color: theme.primary }}>
                                                <th className="fw-semibold">#</th>
                                                <th className="fw-semibold">Received</th>
                                                <th className="fw-semibold">Type</th>
                                                <th className="fw-semibold">Status</th>
                                                <th className="fw-semibold">gateway_event_id</th>
                                                <th className="fw-semibold text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trace.webhook_events.map(ev => (
                                                <React.Fragment key={ev.id}>
                                                    <tr style={{ fontSize: '12.5px' }}>
                                                        <td><code>{ev.id}</code></td>
                                                        <td>{formatDateTime(ev.received_at)}</td>
                                                        <td>{ev.event_type || '—'}</td>
                                                        <td>
                                                            <EventPill status={ev.processing_status} />
                                                            {ev.error_message && (
                                                                <div className="text-danger" style={{ fontSize: '11px' }}>{ev.error_message}</div>
                                                            )}
                                                        </td>
                                                        <td><code style={{ fontSize: '11.5px' }}>{ev.gateway_event_id || '—'}</code></td>
                                                        <td className="text-end">
                                                            <button type="button" className="btn btn-sm btn-light border"
                                                                aria-expanded={expanded === ev.id}
                                                                onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}>
                                                                {expanded === ev.id ? 'Hide payload' : 'View payload'}
                                                            </button>
                                                            <button type="button" className="btn btn-sm ms-2"
                                                                onClick={() => setReplayTarget(ev)}
                                                                style={{ border: `1px solid ${theme.primary}`, color: theme.primary }}>
                                                                Replay
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expanded === ev.id && (
                                                        <tr>
                                                            <td colSpan={6} className="px-0">
                                                                <pre className="mb-0 p-3" style={{
                                                                    background: '#0f172a', color: '#e2e8f0', borderRadius: 6,
                                                                    fontSize: '11.5px', maxHeight: 320, overflow: 'auto'
                                                                }}>{JSON.stringify(ev.payload, null, 2)}</pre>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </Card>
                    </>
                )}
            </div>

            {replayTarget && (
                <ReplayDialog event={replayTarget} theme={theme} busy={replayBusy}
                    onCancel={() => setReplayTarget(null)} onConfirm={doReplay} />
            )}
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-muted text-uppercase" style={{ fontSize: '10.5px', letterSpacing: '0.4px' }}>{label}</div>
            <div>{value}</div>
        </div>
    );
}
